import React from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery } from 'react-query'
import {
  Settings,
  Edit,
  ArrowLeft,
  Loader2,
  Calendar,
  Activity,
  Type,
  CheckCircle,
  XCircle
} from 'lucide-react'
import { dataInputsService } from '@/services/dataInputs'
import { ROUTES } from '@/utils/constants'
import toast from 'react-hot-toast'

const AttributeDetail = () => {
  const { dataInputId, attributeId } = useParams()
  const navigate = useNavigate()

  const {
    data: attribute,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['attribute', dataInputId, attributeId],
    () => dataInputsService.getAttributeById(dataInputId, attributeId),
    {
      onError: (error) => {
        toast.error('Failed to load attribute details')
        console.error('Error loading attribute:', error)
      }
    }
  )

  const {
    data: dataInput,
    isLoading: dataInputLoading
  } = useQuery(
    ['dataInput', dataInputId],
    () => dataInputsService.getById(dataInputId),
    {
      onError: (error) => {
        console.error('Error loading data input:', error)
      }
    }
  )



  if (isLoading || dataInputLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400 mr-2" />
          <span className="text-gray-500">Loading attribute details...</span>
        </div>
      </div>
    )
  }

  if (error || !attribute) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-red-100">
            <Settings className="h-6 w-6 text-red-600" />
          </div>
          <h3 className="mt-2 text-sm font-medium text-gray-900">Attribute not found</h3>
          <p className="mt-1 text-sm text-gray-500">
            The attribute you're looking for doesn't exist or has been deleted.
          </p>
          <div className="mt-6">
            <button
              onClick={() => navigate(`/data-inputs/${dataInputId}`)}
              className="btn-primary btn-sm"
            >
              Back to Data Input
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(`/data-inputs/${dataInputId}`)}
            className="btn-outline btn-sm flex items-center"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Data Input
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{attribute.name}</h1>
            <p className="mt-1 text-sm text-gray-600">
              Attribute details for {dataInput?.name || 'Data Input'}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <Link
            to={`/data-inputs/${dataInputId}/attributes/${attributeId}/edit`}
            className="btn-primary btn-sm flex items-center"
          >
            <Edit className="h-4 w-4 mr-2" />
            Edit Attribute
          </Link>
        </div>
      </div>

      {/* Attribute Information */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Attribute Information</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0 h-12 w-12 rounded-lg bg-primary-100 flex items-center justify-center">
                <Settings className="h-6 w-6 text-primary-600" />
              </div>
              <div>
                <div className="text-sm font-medium text-gray-900">Attribute Name</div>
                <div className="text-sm text-gray-600">{attribute.name}</div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Type className="h-5 w-5 text-gray-400" />
              <div>
                <div className="text-sm font-medium text-gray-900">Data Type</div>
                <div className="text-sm text-gray-600">{attribute.data_type}</div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              {attribute.is_required ? (
                <CheckCircle className="h-5 w-5 text-green-500" />
              ) : (
                <XCircle className="h-5 w-5 text-gray-400" />
              )}
              <div>
                <div className="text-sm font-medium text-gray-900">Required</div>
                <div className="text-sm text-gray-600">
                  {attribute.is_required ? 'Yes' : 'No'}
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Activity className="h-5 w-5 text-gray-400" />
              <div>
                <div className="text-sm font-medium text-gray-900">Status</div>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  attribute.is_active 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {attribute.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Calendar className="h-5 w-5 text-gray-400" />
              <div>
                <div className="text-sm font-medium text-gray-900">Created</div>
                <div className="text-sm text-gray-600">
                  {new Date(attribute.created_at).toLocaleDateString()}
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Calendar className="h-5 w-5 text-gray-400" />
              <div>
                <div className="text-sm font-medium text-gray-900">Last Updated</div>
                <div className="text-sm text-gray-600">
                  {new Date(attribute.updated_at).toLocaleDateString()}
                </div>
              </div>
            </div>
          </div>

          {attribute.label && (
            <div className="mt-6">
              <div className="text-sm font-medium text-gray-900 mb-2">Label</div>
              <p className="text-sm text-gray-600">{attribute.label}</p>
            </div>
          )}

          {attribute.description && (
            <div className="mt-6">
              <div className="text-sm font-medium text-gray-900 mb-2">Description</div>
              <p className="text-sm text-gray-600">{attribute.description}</p>
            </div>
          )}

          {attribute.default_value && (
            <div className="mt-6">
              <div className="text-sm font-medium text-gray-900 mb-2">Default Value</div>
              <p className="text-sm text-gray-600">{attribute.default_value}</p>
            </div>
          )}
        </div>
      </div>


    </div>
  )
}

export default AttributeDetail
